import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { NegotiationInteractionType, SinisterNegotiationService } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { NEGOTIATION_INTERACTION_OPTIONS, getNegotiationInteractionLabel } from "../../constants/negotiationInteractionTypes";
import { FollowUpGenerationOptions } from "../FollowUpGenerationOptions";
import { generateFollowUps } from "../../utils/generateFollowUps";

interface Props {
	open: boolean;
	sinisterId: string;
	onClose: () => void;
	onRegistered: () => void;
}

const DEFAULT_INTERACTION_TYPE: NegotiationInteractionType = NEGOTIATION_INTERACTION_OPTIONS[0].value;

export const ModalRegisterInteraction = ({ open, sinisterId, onClose, onRegistered }: Props) => {
	const toastHandler = useToast();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [formType, setFormType] = useState<NegotiationInteractionType>(DEFAULT_INTERACTION_TYPE);
	const [formReturnAt, setFormReturnAt] = useState("");
	const [formNote, setFormNote] = useState("");
	const [generateFollowUp, setGenerateFollowUp] = useState(true);
	// Follow up externo é visível pro sinistro e pra imobiliária (e dispara e-mail aos contatos
	// cadastrados) — por isso começa desmarcado, é uma escolha explícita de cada lançamento.
	const [generateExternalFollowUp, setGenerateExternalFollowUp] = useState(false);

	useEffect(() => {
		if (open) {
			setGenerateFollowUp(true);
			setGenerateExternalFollowUp(false);
		}
	}, [open]);

	const handleRegisterInteraction = async () => {
		setIsSubmitting(true);
		const result = await trackAsync(
			"negotiation.create_interaction",
			() =>
				SinisterNegotiationService.createInteraction(sinisterId, {
					type: formType,
					note: formNote || undefined,
					returnAt: formType === "awaiting_return" ? formReturnAt : undefined,
				}),
			{ sinisterId }
		);

		if (result instanceof Error) {
			setIsSubmitting(false);
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		const lines = [`Ação registrada: ${getNegotiationInteractionLabel(formType)}`];
		if (formType === "awaiting_return" && formReturnAt) lines.push(`Retornar em: ${format(parseISO(formReturnAt), "dd/MM/yyyy")}`);
		if (formNote) lines.push(formNote);

		await generateFollowUps({
			sinisterId,
			note: lines.map((line) => `<p>${line}</p>`).join(""),
			generateInternal: generateFollowUp,
			generateExternal: generateExternalFollowUp,
			trackLabel: "create_interaction",
		});

		setIsSubmitting(false);
		toastHandler.present({ type: "success", messageOrContent: "Ação registrada com sucesso!", position: "top-right" });
		setFormType(DEFAULT_INTERACTION_TYPE);
		setFormNote("");
		setFormReturnAt("");
		setGenerateExternalFollowUp(false);
		onRegistered();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Registrar Ação
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<TextField
							select
							label="Tipo de ação"
							value={formType}
							onChange={(e) => setFormType(e.target.value as NegotiationInteractionType)}
							fullWidth
						>
							{NEGOTIATION_INTERACTION_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						{formType === "awaiting_return" && (
							<TextField
								label="Retornar em"
								type="date"
								value={formReturnAt}
								onChange={(e) => setFormReturnAt(e.target.value)}
								InputLabelProps={{ shrink: true }}
								fullWidth
							/>
						)}

						<TextField label="Observação" multiline minRows={2} value={formNote} onChange={(e) => setFormNote(e.target.value)} fullWidth />

						<FollowUpGenerationOptions
							generateInternal={generateFollowUp}
							onChangeInternal={setGenerateFollowUp}
							generateExternal={generateExternalFollowUp}
							onChangeExternal={setGenerateExternalFollowUp}
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Cancelar</Button>
					<Button
						variant="contained"
						disabled={isSubmitting || (formType === "awaiting_return" && !formReturnAt)}
						onClick={handleRegisterInteraction}
					>
						Registrar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
